// Suppliers API
import api, { type AxiosResponse } from './client';
import type {
  SupplierProfile,
  SupplierDashboardStats,
  SupplierPerformanceMetric,
  SupplierCertification,
  SupplierMessage,
  CreditScore,
  QueryParams,
} from './types';

export const suppliersAPI = {
  // Supplier Profiles
  getProfiles: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierProfile[] }>> =>
    api.get('/suppliers/profiles/', { params }),

  getProfile: (id: number): Promise<AxiosResponse<SupplierProfile>> =>
    api.get(`/suppliers/profiles/${id}/`),

  updateProfile: (id: number, data: Partial<SupplierProfile>): Promise<AxiosResponse<SupplierProfile>> =>
    api.patch(`/suppliers/profiles/${id}/`, data),

  getDashboardStats: (params?: QueryParams): Promise<AxiosResponse<SupplierDashboardStats>> =>
    api.get('/suppliers/profiles/dashboard_stats/', { params }),

  getTopRated: (): Promise<AxiosResponse<SupplierProfile[]>> =>
    api.get('/suppliers/profiles/top_rated/'),

  // Performance Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierPerformanceMetric[] }>> =>
    api.get('/suppliers/metrics/', { params }),

  // Certifications
  getCertifications: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierCertification[] }>> =>
    api.get('/suppliers/certifications/', { params }),

  // CR-3375: Supplier OTIF Settings
  getOtifSettings: (profileId: number): Promise<AxiosResponse<{
    supplier_id: number;
    supplier_name: string;
    use_custom_otif_window: boolean;
    otif_days_early_allowed: number;
    otif_days_late_allowed: number;
  }>> =>
    api.get(`/suppliers/profiles/${profileId}/otif-settings/`),

  updateOtifSettings: (profileId: number, data: {
    use_custom_otif_window?: boolean;
    otif_days_early_allowed?: number;
    otif_days_late_allowed?: number;
  }): Promise<AxiosResponse<{
    status: string;
    message: string;
    data: {
      supplier_id: number;
      supplier_name: string;
      use_custom_otif_window: boolean;
      otif_days_early_allowed: number;
      otif_days_late_allowed: number;
    };
  }>> =>
    api.patch(`/suppliers/profiles/${profileId}/otif-settings/`, data),

  // Stock Levels
  getStockLevels: (params?: {
    supplier?: number;
    customer?: number;
    part_number?: string;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{
    count: number;
    results: SupplierStockLevel[];
  }>> =>
    api.get('/suppliers/stock-levels/', { params }),

  getStockLevel: (id: number): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.get(`/suppliers/stock-levels/${id}/`),

  createStockLevel: (data: {
    customer: number;
    part_number: string;
    quantity_available: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.post('/suppliers/stock-levels/', data),

  updateStockLevel: (id: number, data: {
    quantity_available?: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.patch(`/suppliers/stock-levels/${id}/`, data),

  deleteStockLevel: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/suppliers/stock-levels/${id}/`),

  bulkUploadStockLevels: (file: File): Promise<AxiosResponse<{
    success: boolean;
    created: number;
    updated: number;
    errors: Array<{ row: number; errors: Record<string, string[]>; data: Record<string, string> }>;
    total_errors: number;
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/suppliers/stock-levels/bulk-upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadStockTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/suppliers/stock-levels/download-template/', {
      responseType: 'blob',
    }),

  exportStockLevels: (params?: { part_number?: string }): Promise<AxiosResponse<Blob>> =>
    api.get('/suppliers/stock-levels/export/', {
      params,
      responseType: 'blob',
    }),

  // Create supplier from onboarding wizard
  createFromWizard: (data: {
    company_name: string;
    company_address?: string;
    // CR-5390: structured address fields. Backend prefers these over the
    // legacy company_address single-line field when both are present.
    address_line_1?: string;
    address_line_2?: string;
    address_city?: string;
    address_region?: string;
    address_postcode?: string;
    address_country?: string;
    description?: string;
    currency?: string;
    annual_spend?: string;
    logo_url?: string;
    customer_id: number;
    customer_ids?: number[];
  }): Promise<AxiosResponse<{
    supplier_id: number;
    company_name: string;
    onboarding_status: string;
  }>> =>
    api.post('/suppliers/profiles/create-from-wizard/', data),

  // Save wizard draft state for resuming onboarding wizard
  saveWizardDraft: (companyId: number, data: {
    form_data: Record<string, unknown>;
    current_step: number;
    highest_step: number;
  }): Promise<AxiosResponse<{ status: string; wizard_draft: Record<string, unknown> }>> =>
    api.patch(`/suppliers/profiles/${companyId}/save-draft/`, data),

  // CR-4056: Toggle supplier active/inactive
  toggleSupplierActive: (id: number, isActive?: boolean): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/suppliers/profiles/${id}/toggle-active/`, isActive !== undefined ? { is_active: isActive } : {}),

  // CR-4161: Credit Scores
  getCreditScores: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CreditScore[] }>> =>
    api.get('/suppliers/credit-scores/', { params }),

  createCreditScore: (data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.post('/suppliers/credit-scores/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateCreditScore: (id: number, data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.patch(`/suppliers/credit-scores/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteCreditScore: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/suppliers/credit-scores/${id}/`),

  // CR-4146: UK Companies House search
  searchCompaniesHouse: (query: string): Promise<AxiosResponse<{
    results: Array<{
      registration_number: string;
      company_name: string;
      address: string;
      incorporated_on: string;
      company_status: string;
      company_type: string;
    }>;
  }>> =>
    api.get('/suppliers/companies-house/search/', { params: { q: query } }),

  // Performance Matrix - monthly OTIF ratings per supplier
  getPerformanceMatrix: (params?: { year?: number }): Promise<AxiosResponse<PerformanceMatrixResponse>> =>
    api.get('/suppliers/performance-matrix/', { params }),

  // Supplier Messages (internal communication)
  getMessages: (params: { supplier: number; customer: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: SupplierMessage[] }>> =>
    api.get('/suppliers/messages/', { params }),

  sendMessage: (data: { supplier: number; customer: number; content: string }): Promise<AxiosResponse<SupplierMessage>> =>
    api.post('/suppliers/messages/', data),
};

export interface PerformanceMatrixRow {
  supplier_id: number;
  Supplier: string;
  SupplierCode: string;
  Business: string;
  Jan: string; Feb: string; Mar: string; Apr: string; May: string; Jun: string;
  Jul: string; Aug: string; Sep: string; Oct: string; Nov: string; Dec: string;
  Jan_pct: number | null; Feb_pct: number | null; Mar_pct: number | null;
  Apr_pct: number | null; May_pct: number | null; Jun_pct: number | null;
  Jul_pct: number | null; Aug_pct: number | null; Sep_pct: number | null;
  Oct_pct: number | null; Nov_pct: number | null; Dec_pct: number | null;
  OverallRating: string;
  OverallPct: number | null;
  TotalLines: number;
  OtifLines: number;
}

export interface PerformanceMatrixResponse {
  year: number;
  rows: PerformanceMatrixRow[];
  total: number;
}

// Stock Level type
export interface SupplierStockLevel {
  id: number;
  supplier: number;
  supplier_name: string;
  customer: number;
  customer_name: string;
  part_number: string;
  description: string;
  quantity_available: number;
  unit_of_measure: string;
  last_updated: string;
  updated_by: number | null;
  updated_by_name: string | null;
  notes: string;
  /** True when call-off is enabled for the (supplier, customer) pair —
   *  the row is read-only on the Stock Levels page in that case. */
  call_off_enabled: boolean;
  created_at: string;
}
